import React from 'react';
import { notificationController } from '@app/controllers/notificationController';
import { useNavigate, useLocation } from 'react-router-dom';
import error404 from '@app/assets/images/error404.svg';
import error403 from '@app/assets/images/error403.svg';
import error500 from '@app/assets/images/error500.svg';

const useApiErrorHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const errorUrl = window.location.href;

  const handleErrorMessages = (rawError: any) => {
    // Normalize the error object - handle different error structures
    const error = normalizeError(rawError);

    switch (error.status) {
      case 401: {
        notificationController.error({
          message: 'Error:',
          description: `${error.message}`,
        });
        navigate('/reset-session');
        break;
      }
      case 403: {
        navigate('/Error', {
          replace: true,
          state: {
            status: '403',
            img: error403,
            errorUrl: errorUrl,
            errorTitle: 'Forbidden',
            errorMessage:
              "You don't have permission to access this resource' is displayed when a web server recognizes a user's request but is unable to allow additional access",
          },
        });
        break;
      }
      case 404: {
        notificationController.error({
          message: 'Page Not Found:',
          description: `${error.message}`,
        });
        navigate('/Error', {
          replace: true,
          state: {
            status: '404',
            img: error404,
            errorUrl: errorUrl,
            errorTitle: 'Page Not Found',
            errorMessage:
              ' The server cannot find the requested resource. Links that lead to a 404 page are often called broken or dead links and can be subject to link rot. A 404 status code only indicates that the resource is missing: not whether the absence is temporary or permanent',
          },
        });
        break;
      }

      case 422: {
        // Extract validation error messages
        let errorMessage = 'Validation error occurred';

        if (error.errors) {
          const errorMessages = Object.values(error.errors).flat();
          errorMessage = errorMessages.join(', ');
        } else if (error.message) {
          errorMessage = error.message;
        }

        try {
          notificationController.error({
            message: 'Validation Error:',
            description: errorMessage,
            duration: 10, // display message 10 seconds
          });
        } catch (notificationError) {
          console.error('Error showing notification:', notificationError);
          // Fallback: show alert if notification fails
          alert(`Validation Error: ${errorMessage}`);
        }
        break;
      }
      case 500: {
        notificationController.error({
          message: 'Internal Server Error:',
          description: `${error.message}`,
        });
        navigate('/Error', {
          replace: true,
          state: {
            status: '500',
            img: error500,
            errorUrl: errorUrl,
            errorTitle: 'Internal Server Error',
            errorMessage:
              'The server has encountered an unexpected condition or configuration problem that prevents it from fulfilling the request made by the browser or client',
          },
        });
        break;
      }

      default: {
        navigate('/Error', {
          replace: true,
          state: {
            status: error.status,
            errorUrl: errorUrl,
            errorTitle: 'oops!',
            errorMessage: error?.message || 'Something went wrong',
          },
        });
        break;
      }
    }
  };

  // Helper function to normalize different error object structures
  const normalizeError = (err: any) => {
    // If it's already normalized, return as-is
    if (err.status && !err.response) {
      return err;
    }

    // Handle Axios-style errors (err.response.data)
    if (err.response) {
      return {
        status: err.response.status,
        message: err.response.data?.message,
        errors: err.response.data?.errors,
        data: err.response.data,
      };
    }

    // Handle fetch-style errors or other formats
    if (err?.status) {
      return {
        status: err.status,
        message: err.message || JSON.stringify(err.errors || err),
        errors: err.errors,
        data: err,
      };
    }

    // If it’s an actual Error object
    if (err instanceof Error) {
      return {
        status: 500,
        message: err.message,
        errors: undefined,
        data: err,
      };
    }

    // Generic error fallback
    return {
      status: 500,
      message: 'An unexpected error occurred',
      errors: undefined,
      data: err,
    };
  };

  return handleErrorMessages;
};

export default useApiErrorHandler;
